/**
 * detalhe.tsx — A PÁGINA DE UMA TAREFA, seja ela de quem for.
 *
 * ─── POR QUE ELA PRECISOU EXISTIR ──────────────────────────────────────────
 *
 * A etapa de uma autorização já tinha página: `/autorizacoes/:id/etapas/:id`,
 * com a conversa e o pedido ao lado. Mas a migration 101 transformou a etapa em
 * TAREFA, com âncora polimórfica — e a **obrigação** (alvará, IPTU, certificado
 * digital) não se pendura em autorização nenhuma. Ela é da loja, e é recorrente.
 *
 * Então, no instante em que a agenda passou a mostrar a tarefa no dia do prazo,
 * clicar num alvará não tinha para onde ir. Era o recado que a ficha #32 deixou
 * na #27 em 15/09, e chegou antes do erro.
 *
 * ─── O QUE ELA NÃO TENTA SER ───────────────────────────────────────────────
 *
 * Não substitui a página da etapa. Tarefa que É etapa de autorização continua
 * abrindo lá: aquela página mostra o pedido, as etapas vizinhas e a fila, e
 * perder isso seria trocar contexto por uniformidade. Esta aqui é para quem não
 * tem esse contexto — e redireciona quando percebe que tem.
 *
 * É de propósito que ela mostre pouco: prazo, dono, estado, adiamentos, o
 * detalhe e o histórico. É o que responde "o que é isto, de quem é, e está
 * atrasado?", que é a pergunta de quem clicou vindo do calendário.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

interface Tarefa {
  id: number;
  tipo_tarefa: string;
  ancora_tipo: string | null;
  ancora_id: number | null;
  autorizacao_id: number | null;
  titulo: string;
  detalhe: string | null;
  responsavel_id: number | null;
  responsavel_nome: string | null;
  estado: string;
  prazo: string | null;
  vencida: boolean;
  adiamentos: number;
  observacao_conclusao: string | null;
  conclusao: string | null;
  concluinte_nome: string | null;
  inicio: string | null;
  criado_em: string | null;
  criador_nome: string | null;
}

/** O mesmo vocabulário da página da etapa — dois nomes para o mesmo estado
 *  seria pior que nenhum. */
function selo(t: Tarefa) {
  if (t.estado === 'feita') return { rotulo: 'Concluída', cor: 'casaa-selo-sucesso' };
  if (t.estado === 'dispensada') return { rotulo: 'Dispensada', cor: 'casaa-selo-neutro' };
  if (t.estado === 'em_andamento') return { rotulo: 'Em andamento', cor: 'casaa-selo-destaque' };
  return { rotulo: 'Não iniciada', cor: 'casaa-selo-neutro' };
}

const NOME_DO_TIPO: Record<string, string> = {
  etapa: 'Etapa de autorização',
  obrigacao: 'Obrigação',
  avulsa: 'Tarefa',
};

export function TarefaDetalhePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tarefa, setTarefa] = useState<Tarefa | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const t = await api.get<Tarefa>(`/tarefas/${id}`);
      // A etapa tem casa melhor: a página da autorização mostra o pedido, as
      // vizinhas e a fila. `replace` para o Voltar não cair aqui de novo.
      if (t.tipo_tarefa === 'etapa' && t.ancora_tipo === 'autorizacoes' && t.ancora_id) {
        navigate(`/autorizacoes/${t.ancora_id}/etapas/${t.id}`, { replace: true });
        return;
      }
      setTarefa(t);
    } catch (e) {
      setErro(
        e instanceof ApiError && e.status === 404
          ? 'Esta tarefa não existe, ou não é sua para ver.'
          : 'Não consegui carregar a tarefa.',
      );
    } finally {
      setCarregando(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function adiar() {
    const motivo = window.prompt(
      'Adiar uma semana. Por quê?\n\nO motivo vira registro — é o que se lê depois.',
    );
    // `null` = desistiu no X; string vazia = clicou OK sem escrever, e aí o
    // backend recusa com a mensagem certa. Não se inventa motivo aqui.
    if (motivo === null) return;
    try {
      await api.post(`/agenda/entradas/tarefas/${id}/adiar`, { dias: 7, motivo });
      toast.success('Adiada em uma semana.');
      carregar();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não consegui adiar.');
    }
  }

  if (carregando) {
    return (
      <CustomMolduraPagina titulo="Tarefa">
        <p className="text-secondary">Carregando…</p>
      </CustomMolduraPagina>
    );
  }

  if (erro || !tarefa) {
    return (
      <CustomMolduraPagina titulo="Tarefa">
        <div className="alert alert-warning">{erro}</div>
      </CustomMolduraPagina>
    );
  }

  const s = selo(tarefa);
  const aberta = tarefa.estado === 'aberta' || tarefa.estado === 'em_andamento';

  return (
    <CustomMolduraPagina
      titulo={tarefa.titulo}
      sobrescrito={NOME_DO_TIPO[tarefa.tipo_tarefa] ?? 'Tarefa'}
      caminho={[{ rotulo: 'Agenda', para: '/agenda/agendamentos' }]}
      acoes={
        aberta ? (
          <button type="button" className="btn btn-sm btn-outline-theme" onClick={adiar}>
            <i className="bi bi-calendar-plus me-1"></i> Adiar uma semana
          </button>
        ) : undefined
      }
    >
      <div className="row g-3">
        <div className="col-12 col-lg-8">
          <div className="card adminuiux-card mb-3">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span className={`badge ${s.cor}`}>{s.rotulo}</span>
                {tarefa.vencida && (
                  <span className="badge casaa-selo-atencao">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    Vencida
                  </span>
                )}
                {tarefa.adiamentos > 0 && (
                  /* Três adiamentos é informação, e é por isso que o número
                     aparece em vez de sumir na contagem. */
                  <span className="badge text-bg-light">
                    adiada {tarefa.adiamentos}×
                  </span>
                )}
              </div>

              {tarefa.detalhe ? (
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {tarefa.detalhe}
                </p>
              ) : (
                <p className="text-secondary mb-0">Sem detalhe escrito.</p>
              )}
            </div>
          </div>

          {tarefa.estado === 'feita' && (
            <div className="card adminuiux-card">
              <div className="card-body">
                <h6 className="mb-2">Como terminou</h6>
                <p className="mb-1" style={{ whiteSpace: 'pre-wrap' }}>
                  {tarefa.observacao_conclusao || '—'}
                </p>
                <p className="small text-secondary mb-0">
                  {tarefa.concluinte_nome} · {formatarData(tarefa.conclusao)}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="col-12 col-lg-4">
          <div className="card adminuiux-card">
            <div className="card-body">
              <dl className="mb-0 small">
                <dt className="text-secondary fw-normal">Prazo</dt>
                <dd className={tarefa.vencida ? 'text-danger fw-medium' : ''}>
                  {tarefa.prazo ? formatarData(tarefa.prazo) : 'sem prazo'}
                </dd>

                <dt className="text-secondary fw-normal">Responsável</dt>
                <dd>{tarefa.responsavel_nome || 'ninguém ainda'}</dd>

                <dt className="text-secondary fw-normal">Começou</dt>
                <dd>{tarefa.inicio ? formatarData(tarefa.inicio) : '—'}</dd>

                <dt className="text-secondary fw-normal">Criada</dt>
                <dd className="mb-0">
                  {formatarData(tarefa.criado_em)}
                  {tarefa.criador_nome ? ` por ${tarefa.criador_nome}` : ''}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </CustomMolduraPagina>
  );
}

export default TarefaDetalhePage;
